'use client';

import { Box, FormLabel, Stack, TextField, Typography } from '@mui/material';
import type { IntakeFieldErrors, IntakeFieldName } from '../intake-validation';
import {
  INTAKE_PLACEHOLDERS,
  intakeFieldLabelSx,
  intakeFieldSx,
} from '../intake-copy';
import { IntakePhoneFields } from './IntakePhoneFields';
import { IntakeConsentRow } from './IntakeConsentRow';
import { IntakeCountryField } from './IntakeCountryField';
import { IntakeEmailHelperText } from './IntakeEmailHelperText';
import { IntakeFamilyNameField } from './IntakeFamilyNameField';
import { useWidgetI18n } from '../../../i18n/WidgetI18nProvider';

export type IntakeFormValues = {
  givenName: string;
  familyName: string;
  noFamilyName: boolean;
  email: string;
  phone: string;
  phoneCountryIso: string;
  currentCountryCode: string;
  marketingConsent: boolean;
};

type Props = {
  values: IntakeFormValues;
  detectedIso: string | null;
  errors: IntakeFieldErrors;
  emailSuggestion: string | null;
  institutionName: string;
  onChange: (field: keyof IntakeFormValues, value: string | boolean) => void;
  onBlurField: (field: IntakeFieldName) => void;
  onApplyEmailSuggestion: () => void;
};

export function IntakeFields({
  values,
  detectedIso,
  errors,
  emailSuggestion,
  institutionName,
  onChange,
  onBlurField,
  onApplyEmailSuggestion,
}: Props) {
  const { t, locale } = useWidgetI18n();

  return (
    <Stack
      spacing={1.25}
      component="fieldset"
      sx={{ border: 0, m: 0, p: 0, minWidth: 0 }}
    >
      <Typography
        component="legend"
        lang={locale}
        sx={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
        }}
      >
        {t('intake.contactDetails')}
      </Typography>

      <Box>
        <FormLabel sx={intakeFieldLabelSx} htmlFor="intake-given-name">
          First name
        </FormLabel>
        <TextField
          id="intake-given-name"
          name="givenName"
          autoComplete="given-name"
          required
          fullWidth
          size="small"
          placeholder={INTAKE_PLACEHOLDERS.givenName}
          value={values.givenName}
          onChange={e => onChange('givenName', e.target.value)}
          onBlur={() => onBlurField('givenName')}
          error={Boolean(errors.givenName)}
          helperText={errors.givenName}
          aria-invalid={Boolean(errors.givenName)}
          sx={intakeFieldSx}
        />
      </Box>

      <IntakeFamilyNameField
        value={values.familyName}
        noFamilyName={values.noFamilyName}
        error={errors.familyName}
        onChange={value => onChange('familyName', value)}
        onToggleNoFamilyName={checked => onChange('noFamilyName', checked)}
        onBlur={() => onBlurField('familyName')}
      />

      <Box>
        <FormLabel sx={intakeFieldLabelSx} htmlFor="intake-email">
          Email address
        </FormLabel>
        <TextField
          id="intake-email"
          name="email"
          type="text"
          autoComplete="email"
          required
          fullWidth
          size="small"
          placeholder={INTAKE_PLACEHOLDERS.email}
          inputProps={{ inputMode: 'email' }}
          value={values.email}
          onChange={e => onChange('email', e.target.value)}
          onBlur={() => onBlurField('email')}
          error={Boolean(errors.email)}
          helperText={
            errors.email || emailSuggestion ? (
              <IntakeEmailHelperText
                error={errors.email}
                suggestion={emailSuggestion}
                onApply={onApplyEmailSuggestion}
              />
            ) : undefined
          }
          aria-invalid={Boolean(errors.email)}
          sx={intakeFieldSx}
        />
      </Box>

      <IntakePhoneFields
        values={values}
        detectedIso={detectedIso}
        errors={errors}
        onChange={onChange}
        onBlurField={onBlurField}
      />

      <IntakeCountryField
        value={values.currentCountryCode}
        detectedIso={detectedIso}
        error={errors.currentCountryCode}
        onChange={iso2 => onChange('currentCountryCode', iso2)}
        onBlur={() => onBlurField('currentCountryCode')}
      />

      <IntakeConsentRow
        institutionName={institutionName}
        checked={values.marketingConsent}
        onChange={checked => onChange('marketingConsent', checked)}
      />
    </Stack>
  );
}
